export type ToolId = 'build' | 'transform';

interface ToolsPaletteOptions {
  canvas: HTMLElement;
  onToolChange(tool: ToolId): void;
}

/** Compact active-tool control with an expandable keyboard/click selector. */
export class ToolsPalette {
  private active: ToolId = 'build';
  private gameVisible = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly options: ToolsPaletteOptions,
  ) {
    this.render();
    document.addEventListener('keydown', (event) => this.onKey(event));
  }

  get activeTool(): ToolId {
    return this.active;
  }

  get isExpanded(): boolean {
    return this.root.classList.contains('expanded');
  }

  setVisible(visible: boolean): void {
    this.gameVisible = visible;
    this.root.classList.toggle('visible', visible || this.isExpanded);
  }

  private render(): void {
    const active = toolInfo(this.active);
    this.root.className = `${this.gameVisible ? 'visible ' : ''}${
      this.active === 'transform' ? 'transform' : ''
    }`;
    this.root.innerHTML = `
      <div class="tools-shell">
        <button type="button" class="tool-button active-tool active"
          aria-label="${active.name}">
          <kbd>${active.key}</kbd><span class="tool-icon">${active.icon}</span>${active.name}
        </button>
        <div class="tools-list">
          ${toolButton('build', this.active)}
          ${toolButton('transform', this.active)}
        </div>
        <button type="button" class="tools-toggle" aria-label="Expand tools">
          <kbd>T</kbd><span class="tool-icon">⋯</span>
        </button>
      </div>
      <div class="transform-hint">
        <kbd>R</kbd> rotate · hold <kbd>Ctrl</kbd> to mirror · click a face button
      </div>`;
    this.root.querySelector<HTMLButtonElement>('.active-tool')!.onclick = () =>
      this.toggleExpanded();
    this.root.querySelector<HTMLButtonElement>('.tools-toggle')!.onclick = () =>
      this.toggleExpanded();
    this.root.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach((button) => {
      button.onclick = () => this.select(button.dataset.tool as ToolId, true);
    });
  }

  private onKey(event: KeyboardEvent): void {
    if (isInventoryOpen()) return;
    const locked = document.pointerLockElement === this.options.canvas;
    if (event.code === 'KeyT' && !event.repeat && (locked || this.isExpanded)) {
      event.preventDefault();
      this.toggleExpanded();
      return;
    }
    if (event.code === 'Escape' && this.isExpanded) {
      this.close(false);
      return;
    }
    if (!locked && !this.isExpanded) return;
    if (!event.repeat && event.code === 'KeyC') {
      event.preventDefault();
      this.select('build', this.isExpanded);
      return;
    }
    if (!event.repeat && event.code === 'KeyX') {
      event.preventDefault();
      this.select('transform', this.isExpanded);
      return;
    }
  }

  private select(tool: ToolId, closeAfter: boolean): void {
    this.active = tool;
    this.options.onToolChange(tool);
    if (closeAfter) {
      this.close(true);
    } else {
      this.render();
    }
  }

  private toggleExpanded(): void {
    if (this.isExpanded) {
      this.close(true);
      return;
    }
    this.render();
    this.root.classList.add('visible', 'expanded');
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private close(relock: boolean): void {
    this.root.classList.remove('expanded');
    this.render();
    this.root.classList.toggle('visible', this.gameVisible);
    if (relock) this.options.canvas.requestPointerLock();
  }
}

function toolButton(tool: ToolId, active: ToolId): string {
  const info = toolInfo(tool);
  return `<button type="button" class="tool-button${tool === active ? ' active' : ''}"
    data-tool="${tool}" aria-label="${info.name}">
    <kbd>${info.key}</kbd><span class="tool-icon">${info.icon}</span>${info.name}
  </button>`;
}

function toolInfo(tool: ToolId): { name: string; key: string; icon: string } {
  return tool === 'build'
    ? { name: 'Build', key: 'C', icon: '🔨' }
    : { name: 'Transform', key: 'X', icon: '↻' };
}

function isInventoryOpen(): boolean {
  return !!document.getElementById('inventory-popup')?.classList.contains('visible')
    || !!document.getElementById('dev-popup')?.classList.contains('visible');
}
